import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import firebase from 'firebase/app';
import 'firebase/firestore';
import { useRegionContext } from '../region/RegionContext';
import { queryAllRegions } from '../utils/queryAllRegions';

var BusinessDataContext = createContext(null);

export function useBusinessData() {
  var ctx = useContext(BusinessDataContext);
  if (!ctx) throw new Error('useBusinessData must be used inside BusinessDataProvider');
  return ctx;
}

export function BusinessDataProvider({ children }) {
  var { db, isGlobal, allRegionDbs } = useRegionContext();

  var [verifiedAll, setVerifiedAll] = useState(null);
  var [verifiedLoading, setVerifiedLoading] = useState(false);
  var [verifiedError, setVerifiedError] = useState(null);

  var loadVerified = useCallback(async function () {
    if (verifiedLoading) return;
    setVerifiedLoading(true);
    setVerifiedError(null);
    try {
      var verifiedQuery = function (regionDb) {
        return regionDb.collection('Business').where('bizVerified', '==', true).limit(500);
      };

      var data;
      if (isGlobal && allRegionDbs) {
        data = await queryAllRegions(allRegionDbs, null, verifiedQuery);
      } else {
        var snapshot = await verifiedQuery(db).get();
        data = snapshot.docs.map(function (doc) { return { id: doc.id, ...doc.data() }; });
      }

      data.sort(function (a, b) {
        var aTime = (a.verifiedAt || a.importedAt)?.toDate?.()?.getTime?.() || 0;
        var bTime = (b.verifiedAt || b.importedAt)?.toDate?.()?.getTime?.() || 0;
        return bTime - aTime;
      });

      setVerifiedAll(data);
    } catch (err) {
      console.error('Error loading verified businesses:', err);
      setVerifiedError('Failed to load verified businesses');
    } finally {
      setVerifiedLoading(false);
    }
  }, [db, isGlobal, allRegionDbs]);

  var verifiedOnly = useMemo(function () {
    if (!verifiedAll) return null;
    return verifiedAll.filter(function (b) { return !b.bizStripeId; });
  }, [verifiedAll]);

  var partners = useMemo(function () {
    if (!verifiedAll) return null;
    return verifiedAll.filter(function (b) { return !!b.bizStripeId; });
  }, [verifiedAll]);

  var refreshVerified = useCallback(function () {
    setVerifiedAll(null);
    return loadVerified();
  }, [loadVerified]);

  var value = useMemo(function () {
    return {
      verifiedOnly: verifiedOnly,
      partners: partners,
      verifiedLoading: verifiedLoading,
      verifiedError: verifiedError,
      loadVerified: loadVerified,
      refreshVerified: refreshVerified,
    };
  }, [verifiedOnly, partners, verifiedLoading, verifiedError, loadVerified, refreshVerified]);

  return (
    <BusinessDataContext.Provider value={value}>
      {children}
    </BusinessDataContext.Provider>
  );
}
